import { css } from "@emotion/react";
import CloseIcon from "@mui/icons-material/Close";
import React from "react";
import { Portal } from "../Portal";
import { hexToRgba } from "../@utils/hexToRgba";
import { resetButtonStyle } from "../Button";
import { useTheme } from "../theme";
interface OverlayStyleProps {
  /**
   * 0以上1以下の数値を背景の透過度を指定します。
   * ベースとなる色はtheme.palette.background.overlay
   *
   * @default 0.9
   */
  opacity: number;
}

export interface OverlayBaseProps extends Partial<OverlayStyleProps> {
  /**
   * このコンポーネントを表示するかどうかを指定します。
   *
   * @default true
   */
  open?: boolean;
  /**
   * このコンポーネントが出現する際のアニメーションを無効化します。
   *
   * @default false
   */
  disableAnimation?: boolean;
  /**
   * このコンポーネントを表示する位置を指定します。
   *
   * @default top
   */
  position?: "top" | "right" | "bottom" | "left";
  /**
   * このコンポーネントが閉じられた場合の挙動を指定します。
   */
  onClose?: () => void;
  /**
   *
   */
  fullScreen?: boolean;
}

export interface OverlayProps
  extends OverlayBaseProps, React.ComponentPropsWithoutRef<"div"> {
  children?: React.ReactNode;
}

const posSx = {
  top: css`
    top: 0;
    left: 0;
    width: 100%;
    &[data-open="false"] {
      opacity: 0;
      transform: translateY(-100%);
    }
  `,
  bottom: css`
    right: 0;
    bottom: 0;
    width: 100%;
    &[data-open="false"] {
      opacity: 0;
      transform: translateY(100%);
    }
  `,
  left: css`
    top: 0;
    left: 0;
    height: 100%;
    &[data-open="false"] {
      opacity: 0;
      transform: translateX(-100%);
    }
  `,
  right: css`
    top: 0;
    right: 0;
    height: 100%;
    &[data-open="false"] {
      opacity: 0;
      transform: translateX(100%);
    }
  `,
} as const;

/**
 * Overlayはなるべく上の階層に配置してください。
 * 特にLayout系のコンポーネントの子要素として使うと、予期しない挙動をする可能性があります。
 * Overlayをなるべく上の階層に配置することは、文章構成としても妥当です。
 */
export const Overlay = ({
  children,
  opacity = 0.9,
  position = "top",
  open = true,
  disableAnimation,
  onClose,
  fullScreen,
  ...props
}: OverlayProps) => {
  const theme = useTheme();
  return (
    <Portal>
      <div
        css={[
          css`
            position: fixed;
            z-index: ${theme.zIndex.drawer};
            box-sizing: border-box;
            display: flex;
            flex-direction: column;
            padding: ${theme.spacing * 2}px;
            margin: 0px;
            color: #fff;
            background-color: ${hexToRgba(
              theme.palette.background.overlay,
              opacity,
            )};
          `,
          posSx[position],
          !disableAnimation &&
            css`
              transition:
                transform ${theme.transitions.duration.shorter},
                opacity ${theme.transitions.duration.standard};
            `,
          fullScreen &&
            css`
              width: 100%;
              height: 100%;
            `,
        ]}
        data-open={open}
        {...props}
      >
        {onClose && (
          <div
            css={css`
              box-sizing: border-box;
              display: flex;
              justify-content: flex-end;
              padding-bottom: ${theme.spacing}px;
            `}
          >
            <button onClick={onClose} type="button" css={resetButtonStyle}>
              <CloseIcon />
            </button>
          </div>
        )}
        {children}
      </div>
    </Portal>
  );
};
